import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const reference = params.get("reference") || params.get("trxref");
  const [state, setState] = useState({ status: "verifying" });

  useEffect(() => {
    if (!reference) {
      setState({ status: "failed" });
      return;
    }
    api.get(`/payments/verify/${reference}`)
      .then(async ({ data }) => {
        if (data.status === "success") {
          await refresh();
          setState({ status: "success", type: data.type });
          return;
        }
        // Fallback: Paystack may still be finalizing — reconcile pending payments.
        const { data: rec } = await api.post("/payments/reconcile").catch(() => ({ data: { reconciled: 0 } }));
        if (rec.reconciled > 0) {
          await refresh();
          setState({ status: "success", type: reference.includes("unlock") ? "unlock" : "application" });
        } else {
          setState({ status: "failed" });
        }
      })
      .catch(() => setState({ status: "failed" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-slate-200 rounded-2xl shadow-xl p-10 max-w-md w-full text-center"
        data-testid="payment-callback-card"
      >
        <div className="flex justify-center mb-6"><Logo /></div>

        {state.status === "verifying" && (
          <>
            <Loader2 className="w-14 h-14 text-[#0B2447] animate-spin mx-auto" />
            <h1 className="font-display text-2xl font-bold text-[#0B2447] mt-6">Verifying your payment…</h1>
            <p className="text-slate-500 mt-2 text-sm">Payment may take up to 60 seconds. Please don't refresh.</p>
          </>
        )}

        {state.status === "success" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-[#0B2447] mt-6">Payment Successful!</h1>
            <p className="text-slate-500 mt-2">
              {state.type === "unlock"
                ? "Your qualifying courses are now unlocked."
                : "Your university application has been submitted."}
            </p>
            <Button
              className="mt-6 w-full bg-[#0B2447] hover:bg-[#153461]"
              data-testid="callback-continue-btn"
              onClick={() => navigate(state.type === "unlock" ? "/dashboard/courses" : "/dashboard/applications")}
            >
              {state.type === "unlock" ? "View My Courses" : "View My Applications"}
            </Button>
          </>
        )}

        {state.status === "failed" && (
          <>
            <XCircle className="w-16 h-16 text-rose-500 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-[#0B2447] mt-6">Payment Not Completed</h1>
            <p className="text-slate-500 mt-2">We couldn't confirm your payment. No changes were made.</p>
            <Button className="mt-6 w-full bg-[#0B2447] hover:bg-[#153461]"
              data-testid="callback-retry-btn"
              onClick={() => navigate("/dashboard/courses")}>
              Back to Dashboard
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
